<script setup>
    import { ref, computed } from 'vue'

    const emit = defineEmits(['close'])
    const props = defineProps({
        show: {
            type: Boolean,
            default: false,
        },
    })

    const selectedPackage = ref('quarterly')
    const isProcessing = ref(false)

    const packages = [
        {
            id: 'monthly',
            name: '1 Month',
            price: 14.99,
            totalPrice: 14.99,
            billingDetails: 'Billed monthly at $14.99',
            pricePerMonth: '$14.99',
        },
        {
            id: 'quarterly',
            name: '3 Months',
            price: 9.99,
            totalPrice: 29.99,
            billingDetails: 'Billed quarterly at $29.99',
            pricePerMonth: '$9.99',
            savingsText: 'SAVE 33%',
            label: '💎 Best Value',
        },
    ]

    const selectedPackageDetails = computed(() => {
        return packages.find(pkg => pkg.id === selectedPackage.value)
    })

    const handleSubscribe = () => {
        isProcessing.value = true

        setTimeout(() => {
            isProcessing.value = false
            emit('close')
        }, 2000)
    }
</script>

<template>
    <div
        v-if="props.show"
        class="fixed inset-0 z-[1200] flex items-start justify-center overflow-y-auto p-4 pt-8 sm:items-center sm:pt-0">
        <div
            class="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity"
            @click="emit('close')"></div>

        <div
            class="animate-in fade-in zoom-in relative max-h-[90vh] w-full max-w-[380px] overflow-hidden overflow-y-auto rounded-3xl border border-white/10 bg-[#0b0b0b] shadow-2xl sm:max-w-md">
            <button
                type="button"
                aria-label="Close"
                class="absolute right-3 top-3 z-10 rounded-full p-2 transition-colors hover:bg-white/10"
                @click="emit('close')">
                <svg
                    class="h-5 w-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            <div class="px-6 pb-5 pt-8 text-center">
                <h2 class="mb-2 text-2xl font-extrabold text-white">
                    Step Into Your Fantasy — 300 Free Tokens
                </h2>
                <p class="text-sm text-neutral-400">
                    Unlimited AI chat & voice • Free daily tokens • Exclusive celebrity voices
                </p>
            </div>

            <div class="flex flex-col gap-3 px-6 pb-5">
                <button
                    v-for="pkg in packages"
                    :key="pkg.id"
                    type="button"
                    class="relative rounded-full px-5 py-4 text-left transition-all"
                    :class="
                        selectedPackage === pkg.id
                            ? 'border-2 border-[#65e8a4] bg-gradient-to-r from-[#0f0f0f] to-[#101f16] shadow-lg shadow-[#65e8a4]/30'
                            : 'border border-white/10 bg-[#0f0f0f] hover:border-white/20'
                    "
                    @click="selectedPackage = pkg.id">
                    <span
                        v-if="pkg.savingsText"
                        class="absolute -right-2 -top-2 rounded-full bg-pink-600 px-2.5 py-1 text-[10px] font-bold uppercase text-white shadow-md">
                        {{ pkg.savingsText }}
                    </span>
                    <span
                        v-if="pkg.label"
                        class="absolute left-[50%] top-1 -translate-x-1/2 text-xs font-semibold text-[#65e8a4]">
                        {{ pkg.label }}
                    </span>
                    <div class="flex items-center justify-between">
                        <div class="flex flex-col">
                            <span class="text-sm font-semibold text-white sm:text-base">{{
                                pkg.name
                            }}</span>
                            <span class="text-[11px] text-neutral-400">{{
                                pkg.billingDetails
                            }}</span>
                        </div>
                        <div class="flex items-baseline gap-1">
                            <span class="text-xl font-bold text-white sm:text-2xl">{{
                                pkg.pricePerMonth
                            }}</span>
                            <span class="text-xs text-neutral-400">/mo</span>
                        </div>
                    </div>
                </button>
            </div>

            <div class="flex items-center justify-center gap-2 px-6 pb-4 text-center">
                <img
                    src="https://aws-lg.naughtycdn.com/mediaAssets/65028/FC_token.png"
                    alt="Token"
                    class="h-4 w-4" />
                <span class="text-sm font-medium text-[#65e8a4]"
                    >Includes 300 free tokens instantly</span
                >
            </div>

            <div class="px-6 pb-6 text-center">
                <button
                    type="button"
                    class="w-full rounded-full bg-gradient-to-r from-white to-gray-100 py-4 text-sm font-semibold text-gray-800 shadow-md transition-transform hover:scale-[1.03] hover:from-gray-50 hover:to-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="isProcessing"
                    @click="handleSubscribe">
                    <span v-if="isProcessing" class="flex items-center justify-center gap-2">
                        <span
                            class="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent"></span>
                        Processing...
                    </span>
                    <span v-else
                        >Unlock Premium — ${{
                            Number(selectedPackageDetails?.totalPrice ?? 0).toFixed(2)
                        }}</span
                    >
                </button>
            </div>

            <div class="mb-4 space-y-1 text-center">
                <p class="text-xs text-neutral-400">No commitments. Cancel anytime.</p>
                <div class="flex items-center justify-center gap-1 text-amber-400">
                    <svg v-for="i in 5" :key="i" class="h-3 w-3 fill-amber-400" viewBox="0 0 24 24">
                        <path
                            d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                    <span class="ml-1 text-xs text-neutral-400">Loved by 50,000+ members</span>
                </div>
            </div>

            <div class="px-6 pb-8">
                <div class="rounded-2xl border border-white/10 bg-[#0f0f0f] p-5">
                    <h3 class="mb-3 text-sm font-semibold text-neutral-300">What You Get</h3>
                    <ul class="space-y-2">
                        <li
                            v-for="feature in [
                                'Unlimited fantasy chat with celebrity voices',
                                '300 welcome tokens + 10 daily free tokens',
                                'Access to premium images, videos & content',
                                'Generate your own fantasies & characters',
                            ]"
                            :key="feature"
                            class="flex items-start gap-2 text-sm text-neutral-300">
                            <svg
                                class="mt-[2px] h-4 w-4 text-[#65e8a4]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="m5 13 4 4L19 7" />
                            </svg>
                            <span>{{ feature }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
